/**
 * Action dependencies hook.
 *
 * This hook provides all the dependencies needed by action performers.
 * It encapsulates the Redux hooks and other dependencies, returning them
 * in a structured object that can be passed to action perform functions.
 */

import { useMemo } from "react";
import { ActionDeps } from "./types";

// Redux hooks
import useModals from "@redux/actions/useModals";
import useNotifications from "@redux/actions/useNotifications";
import useBatchUpdateItemStatus from "@redux/actions/items/useBatchUpdateItemStatus";
import useDuplicateItems from "@redux/actions/items/useDuplicateItems";
import useMoveItems from "@redux/actions/items/useMoveItems";
import useBatchSaveItems from "@actions/items/useBatchSaveItems";
import useSaveMultipleAssetTags from "@redux/actions/assets/useSaveMultipleAssetTags";
import useReplaceAssetFile from "@redux/actions/assets/useReplaceAssetFile";
import useCreateItemAsset from "@redux/actions/items/useCreateItemAsset";
import useBatchProcessAssets from "@redux/actions/assets/useBatchProcessAssets";
import useRemoveAssetThumbnail from "@actions/assets/useRemoveAssetThumbnail";
import useUpdateItem from "@actions/items/useUpdateItem";
import useReplaceAssetFileFromSource from "@actions/assets/useReplaceAssetFileFromSource";
import useCreateAssetsFromSource from "@actions/items/useCreateAssetsFromSource";
import { useAssetDownload } from "@features/asset-export/hooks/useAssetDownload";
import { useAssetCopy } from "@features/asset-export/hooks/useAssetCopy";
import useNavPoint from "@hooks/useNavPoint";

// Core imports
import { useNavigation } from "@thenounproject/lingo-core";
import { useViewModeContext } from "@src/contexts/ViewModeProvider";
import { useSelectionActions } from "@src/contexts/useSelectionHooks";
import useDeleteAssets from "@actions/assets/useDeleteAssets";
import useDeleteAssetItems from "@actions/assets/useDeleteAssetItems";
import useBatchUpdateAssetsMetadata from "@actions/assets/useBatchUpdateAssetsMetadata";
import useSaveAssetMetadata from "@redux/actions/assets/useSaveAssetMetadata";
/**
 * Hook that provides all action dependencies.
 *
 * Returns a memoized ActionDeps object containing all the hooks and functions
 * needed by action performers.
 */
export function useActionDeps(): ActionDeps {
  // Initialize all required hooks
  const { showModal, dismissModal } = useModals();
  const { showNotification } = useNotifications();
  const navigation = useNavigation();
  const { portalId } = useNavPoint();

  // Item actions
  const [batchUpdateItemStatus] = useBatchUpdateItemStatus();
  const [duplicateItems] = useDuplicateItems();
  const [moveItems] = useMoveItems();
  const [batchSaveItems] = useBatchSaveItems();
  const [updateItem] = useUpdateItem();

  // Asset actions
  const [batchSaveTags] = useSaveMultipleAssetTags();
  const [replaceAssetFile] = useReplaceAssetFile();
  const [createItemAsset] = useCreateItemAsset();
  const [replaceAssetFileFromSource] = useReplaceAssetFileFromSource();
  const [createAssetFromSource] = useCreateAssetsFromSource();
  const [batchProcessAssets] = useBatchProcessAssets();
  const [removeAssetThumbnail] = useRemoveAssetThumbnail();
  const [deleteAssets] = useDeleteAssets();
  const [deleteAssetItems] = useDeleteAssetItems();
  const [batchUpdateAssetsMetadata] = useBatchUpdateAssetsMetadata();
  const [saveAssetMetadata] = useSaveAssetMetadata();

  const { setInspectorMode } = useViewModeContext();

  // Download actions
  const assetDownload = useAssetDownload();
  const assetCopy = useAssetCopy();

  const selection = useSelectionActions();
  const editHandler = selection.getEditHandler("kitCanvas");

  // Build and return the deps object
  return useMemo(
    (): ActionDeps => ({
      // Modals & Notifications
      showModal,
      dismissModal,
      showNotification,

      // Navigation
      navigation,
      portalId,

      // Item mutations
      batchUpdateItemStatus,
      duplicateItems,
      moveItems,
      batchSaveItems,
      updateItem,

      // Asset mutations
      batchSaveTags,
      replaceAssetFile,
      createItemAsset,
      replaceAssetFileFromSource,
      createAssetFromSource,
      batchProcessAssets,
      removeAssetThumbnail,
      deleteAssetItems,
      deleteAssets,
      batchUpdateAssetsMetadata,
      saveAssetMetadata,

      // Downloads
      assetDownload,
      assetCopy,

      // View mode
      setInspectorMode,

      // Selection-dependent actions
      editHandler,
      selection,
    }),
    [
      showModal,
      dismissModal,
      showNotification,
      navigation,
      portalId,
      batchUpdateItemStatus,
      duplicateItems,
      moveItems,
      batchSaveItems,
      updateItem,
      batchSaveTags,
      replaceAssetFile,
      createItemAsset,
      replaceAssetFileFromSource,
      createAssetFromSource,
      batchProcessAssets,
      removeAssetThumbnail,
      deleteAssetItems,
      deleteAssets,
      batchUpdateAssetsMetadata,
      saveAssetMetadata,
      assetDownload,
      assetCopy,
      setInspectorMode,
      editHandler,
      selection,
    ]
  );
}
